import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { MeetingFrame } from "@/routes/_authenticated/messaging/meetings/-components/meeting/meeting-frame";
import { EmptyState } from "@/components/app/lists/empty-state";
import { PageHeader } from "@/components/app/shell/page-header";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { guardRoute } from "@/lib/policy/route-guard";
import { Chat } from "@/lib/icons/messaging-icons";
import { orpc } from "@/lib/orpc";
import { usePolicy } from "@/lib/policy";
import { AttendeeList } from "@/routes/_authenticated/messaging/meetings/-components/attendees/attendee-list";
import { MeetingAttendance } from "@/routes/_authenticated/messaging/meetings/-components/recap/meeting-attendance";
import { CalendarFileButton } from "@/routes/_authenticated/messaging/meetings/-components/calendar/calendar-file-button";
import { JoinCard } from "@/routes/_authenticated/messaging/meetings/-components/list/join-card";
import { MeetingFacts } from "@/routes/_authenticated/messaging/meetings/-components/meeting/meeting-facts";
import { MeetingGuestsSection } from "@/routes/_authenticated/messaging/meetings/-components/meeting/meeting-guests-section";
import { MeetingState } from "@/routes/_authenticated/messaging/meetings/-components/meeting/meeting-state";
import { MeetingFiles } from "@/routes/_authenticated/messaging/meetings/-components/recap/meeting-files";
import { MeetingQuestions } from "@/routes/_authenticated/messaging/meetings/-components/recap/meeting-questions";
import { MeetingRecap } from "@/routes/_authenticated/messaging/meetings/-components/recap/meeting-recap";
import { MeetingNotes } from "@/routes/_authenticated/messaging/meetings/-components/recap/meeting-notes";
import { MeetingRecordings } from "@/routes/_authenticated/messaging/meetings/-components/recap/meeting-recordings";
import { OrganizerActions } from "@/routes/_authenticated/messaging/meetings/-components/attendees/organizer-actions";
import { RsvpControl } from "@/routes/_authenticated/messaging/meetings/-components/attendees/rsvp-control";
import { RsvpFollowUp } from "@/routes/_authenticated/messaging/meetings/-components/attendees/rsvp-follow-up";

export const Route = createFileRoute("/_authenticated/messaging/_inbox/meetings/$meetingId")({
  beforeLoad: ({ context }) => guardRoute(context.me, "meetings"),
  component: MeetingPage,
});

const notFound = (error: unknown) => (error as { code?: unknown } | null)?.code === "NOT_FOUND";

/** One meeting: when and where, the call's door, the reader's answer, who is coming, and what the
 * organizer can change. */
function MeetingPage() {
  const { meetingId } = Route.useParams();
  const { me } = usePolicy();
  const meeting = useQuery(orpc.messaging.meetings.get.queryOptions({ input: { meetingId } }));
  if (meeting.isPending) {
    return (
      <MeetingFrame>
        <Skeleton className="h-8 w-72" />
        <div className="grid gap-5 @3xl:grid-cols-[minmax(0,1fr)_21rem] @5xl:grid-cols-[minmax(0,1fr)_24rem]">
          <Skeleton className="h-64 rounded-xl" />
          <Skeleton className="h-48 rounded-xl" />
        </div>
      </MeetingFrame>
    );
  }
  if (meeting.isError) {
    return (
      <MeetingFrame>
        {notFound(meeting.error) ? (
          <EmptyState
            title="This meeting is not on your calendar"
            description="It may have been taken off your invitations, or the link is for someone else's meeting."
            action={
              <Button asChild>
                <Link to="/messaging/meetings">Open the calendar</Link>
              </Button>
            }
          />
        ) : (
          <ReadFailed
            message="Could not load this meeting."
            error={meeting.error}
            onRetry={() => void meeting.refetch()}
            retrying={meeting.isFetching}
          />
        )}
      </MeetingFrame>
    );
  }

  // An instant meeting only carries a call's guest link: nobody gets the organizer's controls.
  const data = meeting.data.instant ? { ...meeting.data, isOrganizer: false } : meeting.data;
  const mine = data.attendees.find((person) => person.userId === me.user.id);
  const cancelled = data.status === "cancelled";
  const answering = Boolean(mine) && !data.isOrganizer && !cancelled;
  return (
    <MeetingFrame>
      <PageHeader
        title={data.title}
        meta={cancelled || data.activeCallId ? <MeetingState meeting={data} /> : undefined}
        actions={
          <>
            <Button asChild variant="outline">
              <Link
                to="/messaging/$conversationId"
                params={{ conversationId: data.conversationId }}
              >
                <Chat className="size-4" />
                Open chat
              </Link>
            </Button>
            {cancelled ? null : <CalendarFileButton meetingId={data.id} />}
            {data.isOrganizer ? <OrganizerActions meeting={data} /> : null}
          </>
        }
      />
      {/* One column on a phone, ordered Join, answer, facts, people; two from lg, where the
          wrappers stop being `contents`. */}
      <div className="flex flex-col gap-5 @3xl:grid @3xl:grid-cols-[minmax(0,1fr)_21rem] @3xl:items-start @5xl:grid-cols-[minmax(0,1fr)_24rem]">
        <div className="contents @3xl:flex @3xl:min-w-0 @3xl:flex-col @3xl:gap-5">
          <Card className="order-3 min-w-0 @3xl:order-none">
            <CardContent className="space-y-5">
              <MeetingFacts meeting={data} />
              <section aria-labelledby="agenda-heading" className="max-w-prose space-y-1.5">
                <h2 id="agenda-heading" className="text-sm font-semibold">
                  Agenda
                </h2>
                {data.agenda ? (
                  <p className="text-sm whitespace-pre-wrap">{data.agenda}</p>
                ) : (
                  <p className="text-muted-foreground text-sm">
                    {data.isOrganizer
                      ? "No agenda yet. Add one with Edit meeting so people come prepared."
                      : "No agenda yet."}
                  </p>
                )}
              </section>
              <MeetingFiles conversationId={data.conversationId} />
              <MeetingRecordings conversationId={data.conversationId} title={data.title} />
            </CardContent>
          </Card>
          <MeetingRecap meeting={data} className="order-3 min-w-0 @3xl:order-none" />
          <Card className="order-5 min-w-0 @3xl:order-none">
            <CardContent>
              <MeetingNotes meeting={data} />
            </CardContent>
          </Card>
          {data.mayHost ? (
            <MeetingQuestions meetingId={data.id} className="order-5 min-w-0 @3xl:order-none" />
          ) : null}
          <Card className="order-4 min-w-0 @3xl:order-none">
            <CardContent className="space-y-6">
              <AttendeeList meeting={data} />
              <MeetingAttendance meeting={data} />
            </CardContent>
          </Card>
        </div>
        <div className="contents @3xl:sticky @3xl:top-0 @3xl:flex @3xl:min-w-0 @3xl:flex-col @3xl:gap-5">
          {cancelled ? null : (
            <div className="order-1 min-w-0 @3xl:order-none">
              <JoinCard meeting={data} />
            </div>
          )}
          <MeetingGuestsSection meeting={data} className="order-2 min-w-0 @3xl:order-none" />
          {answering && mine ? (
            <Card className="order-2 min-w-0 @3xl:order-none">
              <CardContent className="space-y-4">
                <RsvpControl
                  meetingId={data.id}
                  title={data.title}
                  response={mine.response}
                  series={Boolean(data.seriesId)}
                />
                <RsvpFollowUp meeting={data} response={mine.response} />
              </CardContent>
            </Card>
          ) : null}
        </div>
      </div>
    </MeetingFrame>
  );
}
